import { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';

// Decode locally with ZXing, including browsers without native BarcodeDetector (iOS).
export default function WarehouseCameraScanner({ onDetected, onClose, paused = false, children, supplierName }) {
  const video = useRef(null), callbacks = useRef({ onDetected, onClose, paused });
  const locked = useRef(false), lastCode = useRef(''), lastSeen = useRef(0);
  callbacks.current = { onDetected, onClose, paused };
  useEffect(() => {
    if (!paused) {
      locked.current = false;
      video.current?.closest('[role="dialog"]')?.scrollTo({ top: 0 });
    }
  }, [paused]);
  const [error, setError] = useState(''), [ready, setReady] = useState(false);
  const [devices, setDevices] = useState([]), [device, setDevice] = useState('');
  useEffect(() => {
    let disposed = false, stream, controls;
    setError(''); setReady(false);
    const stop = () => {
      disposed = true;
      controls?.stop();
      stream?.getTracks().forEach(track => track.stop());
    };
    const pause = () => { if (document.hidden) { stop(); callbacks.current.onClose(); } };
    document.addEventListener('visibilitychange', pause);
    (async () => {
      try {
        if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
          throw new Error('Buka aplikasi melalui HTTPS agar kamera HP dapat digunakan.');
        }
        const { BrowserMultiFormatReader } = await import('@zxing/browser');
        if (disposed) return;
        stream = await navigator.mediaDevices.getUserMedia({ audio: false, video: device ? { deviceId: { exact: device } } : { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } } });
        if (disposed) { stream.getTracks().forEach(track => track.stop()); return; }
        const cameras = await navigator.mediaDevices.enumerateDevices();
        if (disposed) return;
        setDevices(cameras.filter(camera => camera.kind === 'videoinput'));
        const reader = new BrowserMultiFormatReader(undefined, { delayBetweenScanAttempts: 150 });
        controls = await reader.decodeFromStream(stream, video.current, (result) => {
          if (disposed) return;
          if (!result) {
            // Rearm the same barcode only after it has left the camera view.
            if (Date.now() - lastSeen.current > 800) lastCode.current = '';
            return;
          }
          const code = result.getText().trim();
          if (!code) return;
          const repeated = code === lastCode.current;
          lastSeen.current = Date.now();
          if (callbacks.current.paused || locked.current || repeated) return;
          locked.current = true; lastCode.current = code;
          callbacks.current.onDetected(code);
        });
        if (disposed) controls.stop(); else setReady(true);
      } catch (failure) {
        if (disposed) return;
        stop();
        setError(failure.name === 'NotAllowedError' ? 'Izin kamera ditolak. Izinkan kamera di pengaturan browser, lalu buka pemindai kembali.' : failure.name === 'NotFoundError' ? 'Kamera tidak ditemukan. Gunakan input barcode manual atau scanner.' : failure.name === 'NotReadableError' ? 'Kamera sedang dipakai aplikasi lain. Tutup aplikasi tersebut lalu coba lagi.' : failure.message || 'Kamera gagal dibuka. Coba lagi atau ketik barcode.');
      }
    })();
    return () => { stop(); document.removeEventListener('visibilitychange', pause); };
  }, [device]);
  return <Dialog open onOpenChange={open => { if (!open) onClose(); }}><DialogContent onOpenAutoFocus={e => e.preventDefault()} onCloseAutoFocus={e => e.preventDefault()} onInteractOutside={e => e.preventDefault()} className="bg-ui-card w-full max-w-lg h-[100dvh] max-h-[100dvh] sm:h-auto sm:max-h-[94dvh] !p-3 sm:!p-5 overflow-y-auto !gap-3 !rounded-none sm:!rounded-xl content-start" data-testid="warehouse-camera-dialog"><DialogHeader className="text-left pr-8"><DialogTitle>Scan penerimaan</DialogTitle><DialogDescription>{supplierName} · Scan → isi QTY → simpan → scan berikutnya.</DialogDescription></DialogHeader>
    <video ref={video} muted autoPlay playsInline className={`w-full rounded-lg bg-black object-contain ${paused ? 'h-[18dvh] max-h-36' : 'h-[35dvh] max-h-72'}`} data-testid="warehouse-camera-video" />
    <p className="text-sm" role="status" data-testid="warehouse-camera-status">{error || (ready ? paused ? 'Pembacaan dijeda. Selesaikan barang ini; kamera tetap aktif.' : 'Siap scan berikutnya. Jauhkan barcode sebelumnya sebelum memindai barang yang sama lagi.' : 'Menyiapkan kamera…')}</p>
    {children}
    {devices.length > 1 && !paused && <label className="text-sm">Pilih kamera<select aria-label="Pilih kamera" className="theme-input w-full mt-1" value={device} onChange={e => setDevice(e.target.value)}><option value="">Kamera belakang otomatis</option>{devices.map((camera, index) => <option key={camera.deviceId} value={camera.deviceId}>{camera.label || `Kamera ${index + 1}`}</option>)}</select></label>}
    <button type="button" className="theme-outline min-h-12 w-full" data-testid="warehouse-camera-close" onClick={onClose}>Selesai scan / tutup kamera</button>
  </DialogContent></Dialog>;
}
